(() => {
  // Reusable Klaviyo email sign-up for page.campaign pages. A content session
  // places <div class="ms-campaign-klaviyo-signup" data-klaviyo-list="..."></div>
  // in the page content; this script builds the form from the data attributes.
  // See .agents/skills/shopify-campaign-pages/references/campaign-schema.md.
  // Revision 2026-10-15 accepts profile.subscriptions; 2024-10-15 answers 400 for that field.
  const KLAVIYO_REVISION = '2026-10-15';
  // Same Klaviyo account as the venue-hire form. Deliberately not configurable
  // per page, so page content can never point sign-ups at another account.
  const KLAVIYO_COMPANY = 'XGNcAz';
  const LIST_ID_PATTERN = /^[A-Za-z0-9]{6}$/;
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  // Every listed field is required except phone (optional, but a usable mobile when given), unless the page sets
  // data-required="phone". dob is the 18+ check for every sign-up, with or without a mobile. Order here is the order on the form.
  const ALLOWED_FIELDS = ['first_name', 'email', 'phone', 'dob', 'postcode'];
  const MIN_AGE = 18;
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const DOB_UNDERAGE_MESSAGE = 'You must be 18 or over to sign up.';
  const DOB_INVALID_MESSAGE = 'Please enter your full date of birth.';
  const DOB_FUTURE_MESSAGE = 'Please enter a valid date of birth.';
  const DIALOG_LABEL = 'Age requirement';
  // Name suffix of the follow-up event that records what the visitor was shown after their submission was sent.
  const RESULT_SUFFIX = ' Result';
  // Campaign colour tokens copied onto the dialog, because it lives in <body>, outside .ms-campaign.
  const CAMPAIGN_TOKENS = ['--ms-campaign-accent', '--ms-campaign-accent-text', '--ms-campaign-surface', '--ms-campaign-ink', '--ms-campaign-muted', '--ms-campaign-line'];
  // Australian postcodes: 0200-0299, 0800-0999, 1000-9999.
  const POSTCODE_PATTERN = /^(0[289]\d{2}|[1-9]\d{3})$/;
  const INTRO_MAX = 200;
  const HEADING_MAX = 80;
  const SUCCESS_BUTTON_MAX = 40;
  // Optional button under the thank-you message: a site path (/pages/...) or an https:// address. Anything else
  // (javascript:, http:, //host, spaces) shows no button. Printable ASCII only, and never a backslash: browsers read
  // /\host as //host.
  const SUCCESS_LINK_PATTERN = /^(https:\/\/[!-[\]-~]+|\/(?!\/)[!-[\]-~]*)$/i;
  const DEFAULTS = {
    button: 'Sign me up',
    consent: 'By signing up you agree to receive marketing emails from Manly Spirits Distilling Co. Opt out at any time using the unsubscribe link in any email. You must be 18 or over to sign up.',
    consentWithPhone: 'By signing up you agree to receive marketing emails from Manly Spirits Distilling Co., and text messages if you give your mobile number. Opt out at any time using the unsubscribe link in any email or text. You must be 18 or over to sign up.',
    // A form where the mobile is required: every sign-up gives a number, so the wording does not make texts conditional.
    consentPhoneRequired: 'By signing up you agree to receive marketing emails and text messages from Manly Spirits Distilling Co. Opt out at any time using the unsubscribe link in any email or text. You must be 18 or over to sign up.',
    success: 'Thanks, you’re on the list.',
    error: 'Sorry, that didn’t send. Please check your details and try again.',
    invalid: 'Please check the highlighted fields and try again.',
    source: 'Campaign sign-up form'
  };
  const LABELS = { first_name: 'First name', email: 'Email', phone: 'Mobile', postcode: 'Postcode' };
  const AUTOCOMPLETE = { first_name: 'given-name', email: 'email', phone: 'tel', postcode: 'postal-code' };
  // A custom consent that does not already state the age limit gets one appended.
  const AGE_PATTERN = /18\s*\+|\b18\s+or\s+over\b|\b18\s+years/i;
  const AGE_SENTENCE = 'You must be 18 or over to sign up.';
  // Shown consent is recorded on the profile with this version id. Change the id whenever a default consent text changes.
  const CONSENT_VERSION = 'signup-consent-2026-10-28';
  // One switch: whether phone_number rides on the subscription request. If a live check shows Klaviyo drops
  // subscriptions that contain a phone number, set this to false; the number is still kept in the
  // 'Sign-up SMS Opt-in Mobile' property and on the event.
  const SEND_PHONE_WITH_SUBSCRIPTION = true;
  const HONEYPOT_FIELD = 'extra_note';
  const PRIVACY_URL = '/policies/privacy-policy';
  const REQUEST_TIMEOUT_MS = 15000;
  const HTML_ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

  const clean = (value) => String(value == null ? '' : value).trim();
  const escapeHtml = (value) => String(value).replace(/[&<>"']/g, (character) => HTML_ESCAPES[character]);

  function withAgeStatement(text) {
    return AGE_PATTERN.test(text) ? text : `${text} ${AGE_SENTENCE}`;
  }

  // Reads the marker's data attributes. Unknown field names are dropped and
  // email is always present, so a typo can never produce a form that cannot
  // subscribe anyone. `listValid` is false when the list ID is missing or malformed.
  function parseConfig(dataset) {
    const requested = clean(dataset.fields).split(',').map((name) => name.trim().toLowerCase());
    const list = clean(dataset.klaviyoList);
    const fields = ALLOWED_FIELDS.filter((name) => name === 'email' || requested.includes(name));
    // data-required="phone" makes the mobile required. It only has an effect when the form has a phone field.
    const phoneRequired = fields.includes('phone') && clean(dataset.required).split(',').map((name) => name.trim().toLowerCase()).includes('phone');
    return {
      list,
      listValid: LIST_ID_PATTERN.test(list),
      fields,
      phoneRequired,
      button: clean(dataset.button) || DEFAULTS.button,
      // A form with a mobile field records a text-message opt-in, so the approved wording is fixed:
      // page content cannot replace it (the validator rejects data-consent there, and the script ignores it).
      consent: phoneRequired ? DEFAULTS.consentPhoneRequired
        : fields.includes('phone') ? DEFAULTS.consentWithPhone : withAgeStatement(clean(dataset.consent) || DEFAULTS.consent),
      // Optional sentence at the top of the card, plain text only (it is escaped, never HTML). The validator caps it at 200 characters.
      intro: clean(dataset.intro).slice(0, INTRO_MAX),
      // Optional heading above the intro, inside the form. Plain text, escaped; empty renders nothing.
      heading: clean(dataset.heading).slice(0, HEADING_MAX),
      success: clean(dataset.success) || DEFAULTS.success,
      // Shown together or not at all: a label without a usable link (or a link without a label) adds no button.
      ...successAction(clean(dataset.successButton).slice(0, SUCCESS_BUTTON_MAX), clean(dataset.successLink)),
      error: clean(dataset.error) || DEFAULTS.error,
      source: clean(dataset.source) || DEFAULTS.source,
      event: clean(dataset.event),
      analytics: clean(dataset.analytics).toLowerCase() !== 'off',
      analyticsLabel: clean(dataset.analyticsLabel)
    };
  }

  function successAction(label, link) {
    return label && SUCCESS_LINK_PATTERN.test(link) ? { successButton: label, successLink: link } : { successButton: '', successLink: '' };
  }

  // Australian mobiles only: 04XXXXXXXX, +614XXXXXXXX or 614XXXXXXXX (spaces,
  // dots, dashes and brackets allowed) become +614XXXXXXXX. A trunk 0 after the
  // country code (+61 0412..., +61 (0)412...) is dropped. Anything else,
  // including landlines and overseas numbers, returns ''.
  function normalisePhone(raw) {
    const compact = clean(raw).replace(/[\s().-]/g, '');
    if (/^04\d{8}$/.test(compact)) return `+61${compact.slice(1)}`;
    const international = compact.match(/^\+?610?(4\d{8})$/);
    if (international) return `+61${international[1]}`;
    return '';
  }

  // Sends one request to the Klaviyo client API. A request that has not
  // finished after the timeout is aborted, which callers treat as a failure.
  function makePost(fetchFn, timeoutMs = REQUEST_TIMEOUT_MS) {
    return (path, payload) => {
      let signal;
      if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
        signal = AbortSignal.timeout(timeoutMs);
      } else if (typeof AbortController !== 'undefined') {
        const controller = new AbortController();
        setTimeout(() => controller.abort(), timeoutMs);
        signal = controller.signal;
      }
      return fetchFn(`https://a.klaviyo.com/client/${path}/?company_id=${encodeURIComponent(KLAVIYO_COMPANY)}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', revision: KLAVIYO_REVISION },
        body: JSON.stringify(payload),
        signal
      });
    };
  }

  // Fixed privacy-policy link appended after the consent text. Built with DOM
  // APIs from a document-like object; author text never goes through innerHTML.
  function appendPrivacyLink(paragraph, doc) {
    const link = doc.createElement('a');
    link.setAttribute('href', PRIVACY_URL);
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener');
    link.textContent = 'Privacy policy';
    paragraph.appendChild(doc.createTextNode(' '));
    paragraph.appendChild(link);
    return link;
  }

  function normalisePostcode(raw) {
    const postcode = clean(raw);
    return POSTCODE_PATTERN.test(postcode) ? postcode : '';
  }

  // A real calendar date as YYYY-MM-DD (1900 or later), or ''.
  function normaliseDob(raw) {
    const match = clean(raw).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return '';
    const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
    const date = new Date(Date.UTC(year, month - 1, day));
    const real = year >= 1900 && date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
    return real ? match[0] : '';
  }

  // Today's date in Manly's time zone (Australia/Sydney) as YYYY-MM-DD, read from formatToParts rather than relying on a
  // locale's date layout. `now` is injectable for tests.
  function sydneyToday(now = new Date()) {
    try {
      const parts = Object.fromEntries(new Intl.DateTimeFormat('en-AU', { timeZone: 'Australia/Sydney', year: 'numeric', month: '2-digit', day: '2-digit' })
        .formatToParts(now).map((part) => [part.type, part.value]));
      const iso = `${parts.year}-${parts.month}-${parts.day}`;
      if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
    } catch (error) {
      // An engine without time-zone data: fall through to the fixed offset below.
    }
    // Fallback: UTC+10 (Sydney standard time). It can be a day early during daylight saving, which only makes the age check
    // stricter by at most a day, never more lenient.
    return new Date(now.getTime() + 10 * 3600 * 1000).toISOString().slice(0, 10);
  }

  // '' when the date of birth is fine, otherwise 'invalid' (not a real date, in the future, or incomplete)
  // or 'underage' (under 18 on the submit date, Sydney time). Someone born on 29 February turns 18 on 1 March
  // in a non-leap year (the stricter reading).
  function dobProblem(raw, today) {
    const dob = normaliseDob(raw);
    if (!dob || dob > today) return 'invalid';
    const [year, month, day] = dob.split('-');
    return `${Number(year) + MIN_AGE}-${month}-${day}` <= today ? '' : 'underage';
  }

  // True when the parts of the date that ARE known already prove the person is under 18 on `today`, even though the
  // date is incomplete (YYYY-??-??, YYYY-MM-??) or impossible (2010-02-30): their 18th birthday cannot fall before
  // (year + 18)-(month or 01)-(day or 01), so if that is still in the future they are under 18. A year of
  // currentYear - 18 alone is ambiguous (the birthday could already have passed), so it is not provable without a month/day.
  function provablyUnderage(raw, today) {
    const match = clean(raw).match(/^(\d{4})-(\d{2}|\?\?)-(\d{2}|\?\?)$/);
    if (!match) return false;
    const month = /^(0[1-9]|1[0-2])$/.test(match[2]) ? match[2] : '01';
    const day = /^(0[1-9]|[12]\d|3[01])$/.test(match[3]) ? match[3] : '01';
    return `${Number(match[1]) + MIN_AGE}-${month}-${day}` > today;
  }

  // The date of birth is the 18+ check for every sign-up, so a complete, real date that is not in the future and shows
  // 18 or over on `today` is always required. Returns '' when it is fine, otherwise:
  //   'underage' - under 18, from a complete date or from known parts that already prove it (a year after
  //                currentYear - 18 with the day/month blank, or an impossible date such as 30 Feb 2010)
  //   'future'     - a complete, real date after today
  //   'impossible' - all three parts chosen but not a real date (30 Feb 1990), and not provably under 18
  //   'invalid'    - empty or incomplete (a part is missing), and not provably under 18
  function dobIssue(raw, today) {
    const dob = normaliseDob(raw);
    if (dob) {
      if (dob > today) return 'future';
      return dobProblem(dob, today);
    }
    if (provablyUnderage(raw, today)) return 'underage';
    return /^\d{4}-\d{2}-\d{2}$/.test(clean(raw)) ? 'impossible' : 'invalid';
  }

  // The message to show for a dobIssue result.
  function dobMessage(issue) {
    if (issue === 'underage') return DOB_UNDERAGE_MESSAGE;
    // A date the person did fill in completely gets the 'valid' wording; 'full date' is only for a missing or partial one.
    return issue === 'future' || issue === 'impossible' ? DOB_FUTURE_MESSAGE : DOB_INVALID_MESSAGE;
  }

  // Returns the names of invalid fields, in form order. Every listed field is required except phone, which is
  // optional (unless `phoneRequired`) but must be a usable mobile when given. `today` is the Sydney date, YYYY-MM-DD.
  function validateValues(values, fields, today = sydneyToday(), phoneRequired = false) {
    const errors = [];
    if (fields.includes('first_name') && !clean(values.first_name)) errors.push('first_name');
    if (!EMAIL_PATTERN.test(clean(values.email))) errors.push('email');
    if (fields.includes('phone') && (clean(values.phone) || phoneRequired) && !normalisePhone(values.phone)) errors.push('phone');
    if (fields.includes('dob') && dobIssue(values.dob, today)) errors.push('dob');
    if (fields.includes('postcode') && !normalisePostcode(values.postcode)) errors.push('postcode');
    return ALLOWED_FIELDS.filter((name) => errors.includes(name));
  }

  function profileAttributes(values, config, includePhone = true) {
    const attributes = { email: clean(values.email) };
    if (clean(values.first_name)) attributes.first_name = clean(values.first_name);
    // The phone is only ever sent when the form actually asked for it.
    const phone = config.fields.includes('phone') ? normalisePhone(values.phone) : '';
    if (phone && includePhone) attributes.phone_number = phone;
    return attributes;
  }

  // The mobile to record, only when the form asked for one and it is valid.
  function optInMobile(values, config) {
    return config.fields.includes('phone') ? normalisePhone(values.phone) : '';
  }

  // The text-message opt-in record (submitting with a mobile is the consent), for a later import. Empty unless the form has a
  // phone field and a valid mobile was entered. Klaviyo merges properties, so a later email-only sign-up overwrites the generic
  // 'Sign-up Consent *' keys but must not be able to change what this group says was agreed to: it carries its own wording,
  // version and page, plus a copy of the date of birth for the later age-gated import. No SMS consent is ever sent.
  function smsOptInGroup(values, config, pageUrl, now) {
    const mobile = optInMobile(values, config);
    if (!mobile) return {};
    const dob = config.fields.includes('dob') ? normaliseDob(values.dob) : '';
    return {
      'Sign-up SMS Opt-in': true,
      // From the visitor's device clock, not Klaviyo's: cross-check before an import (see the schema reference).
      'Sign-up SMS Opt-in At': now.toISOString(),
      'Sign-up SMS Opt-in Mobile': mobile,
      'Sign-up SMS Opt-in Consent Version': CONSENT_VERSION,
      'Sign-up SMS Opt-in Consent Text': config.consent,
      'Sign-up SMS Opt-in Source Page': pageUrl,
      ...(dob ? { 'Sign-up SMS Opt-in Date of Birth': dob } : {})
    };
  }

  // A client-generated id shared by the event and the subscription request so they can be reconciled later.
  function newSubmissionId() {
    const c = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
    const bytes = new Uint8Array(16);
    if (c && typeof c.getRandomValues === 'function') c.getRandomValues(bytes);
    else for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  // Email marketing consent is sent as SUBSCRIBED. SMS consent is deliberately NEVER sent:
  // verified live on 22 Oct 2026 that Klaviyo silently drops the whole client subscription
  // (email, postcode and list membership, despite a 202) when it includes subscriptions.sms
  // for an Australian number. Likely cause, not confirmed with Klaviyo: the Australian SMS sender
  // is not set up. Instead the opt-in is recorded in profile properties so it can be imported
  // later (see the schema reference).
  // Postcode goes in location.zip. `now` is injectable for tests.
  function buildSubscriptionPayload(values, config, pageUrl, now = new Date(), submissionId = '') {
    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: config.source,
          profile: {
            data: {
              type: 'profile',
              attributes: {
                ...profileAttributes(values, config, SEND_PHONE_WITH_SUBSCRIPTION),
                ...(normalisePostcode(values.postcode) ? { location: { zip: normalisePostcode(values.postcode) } } : {}),
                properties: {
                  'Sign-up Source Page': pageUrl,
                  // Record exactly what the visitor was shown, and which version of the wording it was.
                  'Sign-up Consent Text': config.consent,
                  'Sign-up Consent Version': CONSENT_VERSION,
                  // Date of birth, the 18+ check for every sign-up: stored whenever the form has the field (submit already
                  // required a complete, real, 18+ date). Never sent as consent or as Klaviyo's age-gated field. It is also recorded on the backup
                  // event (as 'Sign-up Date of Birth'), but never in analytics.
                  ...(config.fields.includes('dob') && normaliseDob(values.dob) ? { 'Date of Birth': normaliseDob(values.dob) } : {}),
                  // Text-message opt-in record (submitting with a mobile is the consent), for a later import. Written only with a
                  // valid mobile, and self-contained: see smsOptInGroup.
                  ...smsOptInGroup(values, config, pageUrl, now),
                  ...(submissionId ? { 'Sign-up Submission ID': submissionId } : {})
                },
                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
              }
            }
          }
        },
        relationships: { list: { data: { type: 'list', id: config.list } } }
      }
    };
  }

  // The sign-up event is the BACKUP RECORD of every submission: live tests showed /client/events is processed even when
  // /client/subscriptions is silently dropped (a 202 that is never processed), so everything that was submitted, and the
  // consent evidence, lives here too and can be read back later through Klaviyo's events API or an export to re-subscribe
  // people. It never carries SMS consent or Klaviyo's age-gated fields. Same keys as the profile properties where they overlap.
  function buildEventPayload(values, config, pageUrl, now = new Date(), submissionId = '') {
    const mobile = optInMobile(values, config);
    const dob = config.fields.includes('dob') ? normaliseDob(values.dob) : '';
    const postcode = config.fields.includes('postcode') ? normalisePostcode(values.postcode) : '';
    const firstName = config.fields.includes('first_name') ? clean(values.first_name) : '';
    return {
      data: {
        type: 'event',
        attributes: {
          properties: {
            'Sign-up List': config.list,
            'Sign-up Source Page': pageUrl,
            'Sign-up Custom Source': config.source,
            'Sign-up Submitted At': now.toISOString(),
            ...(submissionId ? { 'Sign-up Submission ID': submissionId } : {}),
            'Sign-up Email': clean(values.email),
            ...(firstName ? { 'Sign-up First Name': firstName } : {}),
            ...(mobile ? { 'Sign-up Mobile': mobile } : {}),
            ...(postcode ? { 'Sign-up Postcode': postcode } : {}),
            ...(dob ? { 'Sign-up Date of Birth': dob } : {}),
            'Sign-up Consent Text': config.consent,
            'Sign-up Consent Version': CONSENT_VERSION,
            ...smsOptInGroup(values, config, pageUrl, now)
          },
          metric: { data: { type: 'metric', attributes: { name: config.event } } },
          // Email only: if Klaviyo disliked a phone number on the profile it could reject the whole event, and the backup must
          // not depend on that. Nothing is lost: the first name and the mobile are event properties above.
          profile: { data: { type: 'profile', attributes: { email: clean(values.email) } } },
          // Klaviyo de-duplicates events with the same unique_id.
          ...(submissionId ? { unique_id: submissionId } : {})
        }
      }
    };
  }

  // What the visitor was shown after their submission was sent, so a recovery only re-subscribes people who were told it worked.
  // Deliberately minimal: the submission id, the outcome, and the email on the profile. No mobile, no date of birth, no other PII.
  // Outcomes: shown_success, shown_error (rejected, failed), shown_phone_error (Klaviyo rejected the mobile), timeout.
  function buildResultEventPayload(values, config, submissionId, outcome) {
    return {
      data: {
        type: 'event',
        attributes: {
          properties: { 'Sign-up Submission ID': submissionId, 'Sign-up Outcome': outcome },
          metric: { data: { type: 'metric', attributes: { name: config.event + RESULT_SUFFIX } } },
          profile: { data: { type: 'profile', attributes: { email: clean(values.email) } } },
          unique_id: submissionId + ':result'
        }
      }
    };
  }

  // Non-identifying context only: no name, email, phone or postcode.
  function buildAnalyticsPayload(values, config, pageUrl) {
    return {
      form: 'campaign_signup',
      label: config.analyticsLabel || config.event || config.source,
      list_id: config.list,
      has_first_name: Boolean(clean(values.first_name)),
      has_phone: config.fields.includes('phone') && Boolean(normalisePhone(values.phone)),
      has_postcode: Boolean(normalisePostcode(values.postcode)),
      page_url: pageUrl
    };
  }

  // Three selects (day, month, year): reliable on iOS Safari, labelled for screen readers, autofill-aware, and no
  // date-picker quirks. The legend explains when it is needed.
  function buildDobMarkup(prefix, currentYear) {
    const select = (part, label, autocomplete, options) => `<span class="ms-campaign-signup-form__dob-part"><label class="ms-campaign-signup-form__sr" for="${prefix}-dob_${part}">${label}</label>`
      + `<select id="${prefix}-dob_${part}" name="dob_${part}" autocomplete="${autocomplete}" required aria-required="true"><option value="">${label}</option>${options}</select></span>`;
    const days = Array.from({ length: 31 }, (unused, index) => `<option value="${index + 1}">${index + 1}</option>`).join('');
    const months = MONTHS.map((month, index) => `<option value="${index + 1}">${month}</option>`).join('');
    const years = Array.from({ length: currentYear - 1899 }, (unused, index) => `<option value="${currentYear - index}">${currentYear - index}</option>`).join('');
    return `<fieldset class="ms-campaign-signup-form__dob" data-campaign-signup-dob><legend>Date of birth <span>(to confirm you’re 18+)</span></legend>`
      + `<div class="ms-campaign-signup-form__dob-row">${select('day', 'Day', 'bday-day', days)}${select('month', 'Month', 'bday-month', months)}${select('year', 'Year', 'bday-year', years)}</div></fieldset>`;
  }

  // Combines the three selects into YYYY-MM-DD. '' when none is chosen. A chosen year is kept even when the month or day
  // is missing (YYYY-??-??, YYYY-MM-??) so a year that proves the person is under 18 is not lost; 'incomplete' when
  // there is no year but a month or day was chosen.
  function dobFromParts(day, month, year) {
    const [y, m, d] = [clean(year), clean(month), clean(day)];
    if (!y && !m && !d) return '';
    if (!y) return 'incomplete';
    return `${y.padStart(4, '0')}-${m ? m.padStart(2, '0') : '??'}-${d ? d.padStart(2, '0') : '??'}`;
  }

  // `uid` keeps element IDs unique when a page holds more than one form.
  // The status line is always in the DOM (empty) so screen readers announce
  // the text when it is filled in, and so showing it does not shift the layout.
  function buildFormMarkup(config, uid, currentYear = Number(sydneyToday().slice(0, 4))) {
    const prefix = `ms-signup-${uid}`;
    const fields = config.fields.map((name) => {
      if (name === 'dob') return buildDobMarkup(prefix, currentYear);
      const type = name === 'email' ? 'email' : name === 'phone' ? 'tel' : 'text';
      const extra = name === 'postcode' ? ' inputmode="numeric" maxlength="4"' : '';
      const optional = name === 'phone' && !config.phoneRequired;
      return `<p class="ms-campaign-signup-form__field"><label for="${prefix}-${name}">${LABELS[name]}${optional ? ' <span>(optional)</span>' : ''}</label>`
        + `<input id="${prefix}-${name}" name="${name}" type="${type}" autocomplete="${AUTOCOMPLETE[name]}"${extra}${optional ? '' : ' required aria-required="true"'}></p>`;
    }).join('');
    const heading = config.heading ? `<h2 class="ms-campaign-signup-form__heading">${escapeHtml(config.heading)}</h2>` : '';
    const intro = config.intro ? `<p class="ms-campaign-signup-form__intro">${escapeHtml(config.intro)}</p>` : '';
    return `<form class="ms-campaign-signup-form" data-campaign-signup-form novalidate>${heading}${intro}${fields}`
      + `<p class="ms-campaign-signup-form__trap" aria-hidden="true"><label for="${prefix}-note">Leave this field empty</label>`
      + `<input id="${prefix}-note" name="${HONEYPOT_FIELD}" type="text" tabindex="-1" autocomplete="off"></p>`
      + `<button class="ms-campaign-btn ms-action ms-campaign-signup-form__submit" type="submit" data-campaign-signup-submit>${escapeHtml(config.button)}</button>`
      + `<p class="ms-campaign-signup-form__consent">${escapeHtml(config.consent)}</p>`
      + '<p class="ms-campaign-signup-form__status" data-campaign-signup-status role="alert"></p></form>'
      + '<div class="ms-campaign-signup-form__success" data-campaign-signup-success tabindex="-1" role="status" hidden><p></p>'
      + (config.successButton ? `<a class="ms-campaign-btn ms-action ms-campaign-signup-form__success-link" href="${escapeHtml(config.successLink)}">${escapeHtml(config.successButton)}</a>` : '')
      + '</div>';
  }

  // True when a 400 response body names the phone number as the invalid field.
  async function rejectsPhone(response) {
    try {
      const body = typeof response.json === 'function' ? await response.json() : null;
      return Array.isArray(body?.errors) && body.errors.some((error) => /\/phone_number$/.test(String(error?.source?.pointer || '')));
    } catch (error) {
      return false;
    }
  }

  // Runs one submission. Dependencies are injected so the success and failure
  // paths can be tested without a browser or network. Resolves to
  // { ok: true } or { ok: false, reason: 'invalid' | 'send', errors? }.
  async function submitSignup(values, config, deps) {
    // A filled honeypot is a bot: look successful, send nothing.
    if (deps.honeypot) return { ok: true, skipped: true };
    const today = sydneyToday(deps.now ? deps.now() : new Date());
    const errors = validateValues(values, config.fields, today, config.phoneRequired);
    if (errors.length) {
      // Under 18 blocks the whole submit (the consent text says 18+ to sign up), with its own message.
      const dob = errors.includes('dob') ? dobIssue(values.dob, today) : '';
      return { ok: false, reason: 'invalid', errors, ...(dob ? { dob } : {}) };
    }
    const now = deps.now ? deps.now() : new Date();
    const submissionId = deps.submissionId ? deps.submissionId() : newSubmissionId();
    // The event is the backup record, so it goes out FIRST and independently of the subscription: a dropped, rejected or
    // timed-out subscription never loses it. The visitor sees success or failure from the subscription alone.
    if (config.event) {
      try {
        Promise.resolve(deps.post('events', buildEventPayload(values, config, deps.pageUrl, now, submissionId))).catch(() => {});
      } catch (error) {}
    }
    let outcome;
    let result;
    try {
      // No retry without the phone: that would drop the mobile and its opt-in record.
      const response = await deps.post('subscriptions', buildSubscriptionPayload(values, config, deps.pageUrl, now, submissionId));
      if (response.ok) {
        outcome = 'shown_success';
        result = { ok: true };
      } else if (response.status === 400 && await rejectsPhone(response)) {
        // Klaviyo can reject a number the browser accepted; name the field instead of a generic error.
        outcome = 'shown_phone_error';
        result = { ok: false, reason: 'invalid', errors: ['phone'], server: true };
      } else {
        outcome = 'shown_error';
        result = { ok: false, reason: 'send' };
      }
    } catch (error) {
      outcome = error && (error.name === 'TimeoutError' || error.name === 'AbortError') ? 'timeout' : 'shown_error';
      result = { ok: false, reason: 'send' };
    }
    // Fire and forget: record what the visitor is about to be shown. It never affects the visible result.
    if (config.event) {
      try {
        Promise.resolve(deps.post('events', buildResultEventPayload(values, config, submissionId, outcome))).catch(() => {});
      } catch (error) {}
    }
    if (result.ok && config.analytics) {
      // Forwarded by a Shopify custom pixel once one subscribes to this event name.
      try { deps.publish('campaign_signup_submitted', buildAnalyticsPayload(values, config, deps.pageUrl)); } catch (error) {}
    }
    return result;
  }

  // ---- Under-18 dialog -------------------------------------------------------------------------------------
  // An under-18 date of birth is not a typo to fix, so it is shown as a modal dialog (not an inline field error, and
  // focus never goes to the date selects). A plain div with role="dialog" and aria-modal is used instead of <dialog>
  // because it behaves the same on every iOS Safari version we care about. One dialog per form, built on first use.
  function buildDialogMarkup(uid) {
    const id = `ms-signup-${uid}-dialog`;
    return `<div class="ms-campaign-signup-dialog" id="${id}" hidden>`
      // A short hidden label names the dialog; the message is its description, so it is announced once.
      + `<div class="ms-campaign-signup-dialog__box" role="dialog" aria-modal="true" aria-labelledby="${id}-label" aria-describedby="${id}-message">`
      + `<span class="ms-campaign-signup-form__sr" id="${id}-label">${DIALOG_LABEL}</span>`
      + `<p class="ms-campaign-signup-dialog__message" id="${id}-message">${escapeHtml(DOB_UNDERAGE_MESSAGE)}</p>`
      + '<button type="button" class="ms-campaign-btn ms-action ms-campaign-signup-dialog__close" data-campaign-signup-dialog-close>Close</button>'
      + '</div></div>';
  }

  // Sets `inert` on every child of `parent` (the page body) except `except` (the dialog), skipping any that were already
  // inert, and returns a function that removes only what this call added.
  function inertSiblings(parent, except) {
    const changed = [];
    Array.from(parent.children).forEach((element) => {
      if (element === except || element.hasAttribute('inert')) return;
      element.setAttribute('inert', '');
      changed.push(element);
    });
    return () => changed.forEach((element) => element.removeAttribute('inert'));
  }

  // Where focus goes when the dialog closes: the first invalid field that is not part of the date of birth, else the
  // submit button. `invalidElements` are the form's elements currently marked aria-invalid, in DOM order.
  function pickReturnFocus(invalidElements, submit) {
    return Array.from(invalidElements).find((element) => !/^dob_/.test(String(element.name || ''))) || submit;
  }

  // Copies the campaign colour custom properties from `source` (a CSSStyleDeclaration of the .ms-campaign ancestor) onto
  // `target` (the dialog overlay), skipping any that are not set.
  function copyCampaignTokens(source, target) {
    CAMPAIGN_TOKENS.forEach((name) => {
      const value = String(source.getPropertyValue(name) || '').trim();
      if (value) target.style.setProperty(name, value);
    });
  }

  // Behaviour of one dialog, with its elements and side effects injected so it can be tested without a browser.
  // The Close button is the only control, so trapping focus means keeping focus on it.
  function createDialogController({ overlay, closeButton, getReturnFocus, lockScroll, unlockScroll, inertBackground = () => () => {} }) {
    let isOpen = false;
    let restoreBackground = null;
    const controller = {
      get isOpen() { return isOpen; },
      show() {
        if (isOpen) return;
        isOpen = true;
        overlay.hidden = false;
        lockScroll();
        // Everything else on the page becomes inert, so a screen reader's virtual cursor cannot leave the dialog either.
        restoreBackground = inertBackground();
        closeButton.focus();
      },
      hide() {
        if (!isOpen) return;
        isOpen = false;
        overlay.hidden = true;
        unlockScroll();
        // The page must be live again before focus can go back into it.
        if (restoreBackground) restoreBackground();
        restoreBackground = null;
        // To the first remaining non-date error, else the submit button; never the date.
        const target = getReturnFocus();
        if (target) target.focus();
      },
      onKeydown(event) {
        if (!isOpen) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          controller.hide();
        } else if (event.key === 'Tab') {
          event.preventDefault();
          closeButton.focus();
        }
      },
      onFocusIn(event) {
        if (isOpen && !overlay.contains(event.target)) closeButton.focus();
      }
    };
    return controller;
  }

  // Turns an invalid submit result into UI actions via callbacks. Under 18 opens the dialog and never marks, hints or
  // focuses the date; every other error keeps the inline behaviour (aria-invalid, status line, focus on the first one).
  function presentInvalid(result, ui) {
    const underage = result.dob === 'underage';
    const names = underage ? result.errors.filter((name) => name !== 'dob') : result.errors;
    names.forEach((name) => ui.markInvalid(name));
    const hints = [];
    if (names.includes('dob')) hints.push(dobMessage(result.dob));
    if (names.includes('phone')) hints.push('Mobile numbers look like 0412 345 678.');
    if (names.includes('postcode')) hints.push('Postcodes have 4 digits.');
    ui.setStatus(names.length ? [DEFAULTS.invalid, ...hints].join(' ') : '');
    if (underage) ui.openDialog();
    else if (names.length) ui.focusField(names[0]);
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { parseConfig, successAction, normalisePhone, validateValues, buildSubscriptionPayload, buildEventPayload, buildAnalyticsPayload, buildFormMarkup, submitSignup, escapeHtml, withAgeStatement, normalisePostcode, normaliseDob, dobProblem, dobIssue, dobMessage, smsOptInGroup, newSubmissionId, buildResultEventPayload, RESULT_SUFFIX, buildDialogMarkup, createDialogController, presentInvalid, inertSiblings, pickReturnFocus, copyCampaignTokens, CAMPAIGN_TOKENS, DIALOG_LABEL, provablyUnderage, sydneyToday, dobFromParts, DOB_UNDERAGE_MESSAGE, DOB_INVALID_MESSAGE, DOB_FUTURE_MESSAGE, CONSENT_VERSION, makePost, appendPrivacyLink, HONEYPOT_FIELD };
  }
  if (typeof document === 'undefined') return;

  const post = makePost((url, options) => fetch(url, options));
  const publish = (name, payload) => window.Shopify?.analytics?.publish?.(name, payload);

  // Page scroll lock while a dialog is open (one dialog can be open at a time).
  let savedOverflow = null;
  const lockPageScroll = () => {
    if (savedOverflow !== null) return;
    savedOverflow = { html: document.documentElement.style.overflow, body: document.body.style.overflow };
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  };
  const unlockPageScroll = () => {
    if (savedOverflow === null) return;
    document.documentElement.style.overflow = savedOverflow.html;
    document.body.style.overflow = savedOverflow.body;
    savedOverflow = null;
  };

  function initSignup(root, uid) {
    const config = parseConfig(root.dataset);
    if (!config.listValid) {
      // A misconfigured marker must never render a form that cannot work.
      root.hidden = true;
      return;
    }
    root.innerHTML = buildFormMarkup(config, uid);
    root.dataset.ready = 'true';
    const form = root.querySelector('[data-campaign-signup-form]');
    const submit = root.querySelector('[data-campaign-signup-submit]');
    const status = root.querySelector('[data-campaign-signup-status]');
    const success = root.querySelector('[data-campaign-signup-success]');
    let dialog = null;
    // Built on first use and appended to <body>, so no ancestor's transform or stacking context can trap it.
    const ensureDialog = () => {
      if (dialog) return dialog;
      const host = document.createElement('div');
      host.innerHTML = buildDialogMarkup(uid);
      const overlay = host.firstElementChild;
      document.body.appendChild(overlay);
      const closeButton = overlay.querySelector('[data-campaign-signup-dialog-close]');
      const campaign = root.closest('.ms-campaign');
      if (campaign) copyCampaignTokens(window.getComputedStyle(campaign), overlay);
      dialog = createDialogController({
        overlay,
        closeButton,
        getReturnFocus: () => pickReturnFocus(form.querySelectorAll('[aria-invalid="true"]'), submit),
        lockScroll: lockPageScroll,
        unlockScroll: unlockPageScroll,
        inertBackground: () => inertSiblings(document.body, overlay)
      });
      closeButton.addEventListener('click', () => dialog.hide());
      document.addEventListener('keydown', dialog.onKeydown);
      document.addEventListener('focusin', dialog.onFocusIn);
      return dialog;
    };
    appendPrivacyLink(root.querySelector('.ms-campaign-signup-form__consent'), document);

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (submit.disabled) return;
      const data = new FormData(form);
      const values = {};
      config.fields.forEach((name) => {
        values[name] = name === 'dob' ? dobFromParts(data.get('dob_day'), data.get('dob_month'), data.get('dob_year')) : clean(data.get(name));
      });
      form.querySelectorAll('[aria-invalid]').forEach((field) => field.removeAttribute('aria-invalid'));
      status.textContent = '';
      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');
      const result = await submitSignup(values, config, {
        post,
        publish,
        honeypot: clean(data.get(HONEYPOT_FIELD)),
        pageUrl: window.location.origin + window.location.pathname
      });
      submit.removeAttribute('aria-busy');
      if (result.ok) {
        // Hold the card at its current height so swapping in the thank-you does not shift the page.
        root.style.minHeight = `${root.offsetHeight}px`;
        form.hidden = true;
        success.querySelector('p').textContent = config.success;
        success.hidden = false;
        success.focus();
        return;
      }
      submit.disabled = false;
      if (result.reason === 'invalid') {
        const targets = (name) => (name === 'dob' ? ['dob_day', 'dob_month', 'dob_year'] : [name]).map((id) => form.elements[id]);
        presentInvalid(result, {
          markInvalid: (name) => targets(name).forEach((element) => element?.setAttribute('aria-invalid', 'true')),
          setStatus: (text) => { status.textContent = text; },
          focusField: (name) => targets(name)[0]?.focus(),
          openDialog: () => ensureDialog().show()
        });
      } else {
        status.textContent = config.error;
      }
    });
  }

  const start = () => document.querySelectorAll('.ms-campaign-klaviyo-signup').forEach((root, index) => initSignup(root, index + 1));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
